import React, { useState } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Grid, 
  TextField,
  Avatar,
  Button,
  Paper,
  Divider,
  IconButton
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import { Link } from 'react-router-dom';

// Icons
import PersonIcon from '@mui/icons-material/Person';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import SecurityIcon from '@mui/icons-material/Security';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

// Custom components
import ProfileCard, { ProfileSection, ProfileField } from './common/ProfileCard';
import { useAuth } from '../contexts/AuthContext';
import AddSponsor from './AddSponsor';

// Animation
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const AvatarWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  marginBottom: theme.spacing(3),
  width: 120,
  height: 120,
  margin: '0 auto',
}));

const ProfileAvatar = styled(Avatar)(({ theme }) => ({
  width: 120,
  height: 120,
  border: `4px solid ${theme.palette.background.paper}`,
  boxShadow: theme.shadows[3],
}));

const UploadButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: 0,
  bottom: 0,
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const Profile = () => {
  const { profile } = useAuth();
  
  // Personal information state - Initialize with real user data
  const [personalInfo, setPersonalInfo] = useState({
    firstName: profile?.first_name || '',
    lastName: profile?.last_name || '',
    email: profile?.email || '',
    phone: profile?.phone || '',
    dateOfBirth: profile?.date_of_birth || '',
    address: profile?.address || '',
    city: profile?.city || '',
    state: profile?.state || '',
    zipCode: profile?.postal_code || '',
    emergencyContact: profile?.emergency_contact_name || '',
    emergencyPhone: profile?.emergency_contact_phone || ''
  });

  // Health information state - Initialize with real user data
  const [healthInfo, setHealthInfo] = useState({
    height: '',
    weight: '',
    bloodType: profile?.blood_type || '',
    allergies: profile?.allergies?.join(', ') || '',
    medications: '',
    conditions: profile?.medical_conditions?.join(', ') || '',
    primaryPhysician: ''
  });

  // Insurance information state - Initialize empty (to be filled by user)
  const [insuranceInfo, setInsuranceInfo] = useState({
    provider: '',
    policyNumber: '',
    groupNumber: '',
    primaryHolder: '',
    coverageType: '',
    effectiveDate: ''
  });

  // Handle personal info changes
  const handlePersonalInfoChange = (e) => {
    const { name, value } = e.target;
    setPersonalInfo(prev => ({ ...prev, [name]: value }));
  };

  // Handle health info changes
  const handleHealthInfoChange = (e) => {
    const { name, value } = e.target;
    setHealthInfo(prev => ({ ...prev, [name]: value }));
  };

  // Handle insurance info changes
  const handleInsuranceInfoChange = (e) => {
    const { name, value } = e.target;
    setInsuranceInfo(prev => ({ ...prev, [name]: value }));
  };

  // Handle personal info save
  const handlePersonalInfoSave = (action, originalData) => {
    if (action === 'get') {
      return { ...personalInfo };
    } else if (action === 'cancel' && originalData) {
      setPersonalInfo(originalData);
    } else if (action === 'save') {
      if (import.meta.env.DEV) console.log('Saving personal info:', personalInfo);
      // Here you would typically make an API call to save the data
    }
  };

  // Handle health info save
  const handleHealthInfoSave = (action, originalData) => {
    if (action === 'get') {
      return { ...healthInfo };
    } else if (action === 'cancel' && originalData) {
      setHealthInfo(originalData);
    } else if (action === 'save') {
      if (import.meta.env.DEV) console.log('Saving health info:', healthInfo);
      // Here you would typically make an API call to save the data
    }
  };

  // Handle insurance info save
  const handleInsuranceInfoSave = (action, originalData) => {
    if (action === 'get') {
      return { ...insuranceInfo };
    } else if (action === 'cancel' && originalData) {
      setInsuranceInfo(originalData);
    } else if (action === 'save') {
      if (import.meta.env.DEV) console.log('Saving insurance info:', insuranceInfo);
      // Here you would typically make an API call to save the data
    }
  };

  return (
    <Box 
      sx={{ 
        flexGrow: 1, 
        bgcolor: 'background.default', 
        py: 4,
        animation: `${fadeIn} 0.5s ease-out`,
      }}
    >
      <Container maxWidth="lg" sx={{ px: { xs: 1, sm: 2, md: 0 }, width: '100%', maxWidth: '100%' }}>
        <Box sx={{ maxWidth: 700, mx: 'auto', width: '100%' }}>
          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
            <Button 
              component={Link} 
              to="/dashboard" 
              startIcon={<ArrowBackIcon />}
              sx={{ mr: 2 }}
            >
              Back to Dashboard
            </Button>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
              My Profile
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, width: '100%' }}>
            {/* Profile summary */}
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2, width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
              <AvatarWrapper>
                <ProfileAvatar 
                  alt={`${personalInfo.firstName} ${personalInfo.lastName}`}
                  src="/path/to/avatar.jpg" // Placeholder, would be user's actual avatar
                >
                  {personalInfo.firstName.charAt(0)}{personalInfo.lastName.charAt(0)}
                </ProfileAvatar>
                <UploadButton size="small" aria-label="upload picture">
                  <PhotoCameraIcon fontSize="small" />
                </UploadButton>
              </AvatarWrapper>
              
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                {personalInfo.firstName} {personalInfo.lastName}
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {personalInfo.email}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ textAlign: 'left', mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Account Information
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Member Since</Typography>
                  <Typography variant="body2">
                    {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">Last Login</Typography>
                  <Typography variant="body2">
                    {profile?.last_login ? new Date(profile.last_login).toLocaleDateString() : 'Today'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body2" color="text.secondary">Account Type</Typography>
                  <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                    {profile?.tenant_role || 'Patient'}
                  </Typography>
                </Box>
              </Box>
              
              <Button 
                variant="outlined" 
                fullWidth 
                component={Link} 
                to="/settings"
                sx={{ mt: 3 }}
              >
                Account Settings
              </Button>
            </Paper>
            {/* Quick Links */}
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2, width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Quick Links
              </Typography>
              <Box component="ul" sx={{ p: 0, m: 0, listStyle: 'none' }}>
                <Box component="li" sx={{ mb: 1.5 }}>
                  <Link to="/appointments" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                    <Typography variant="body2" sx={{ '&:hover': { color: 'primary.main' } }}>
                      My Appointments
                    </Typography>
                  </Link>
                </Box>
                <Box component="li" sx={{ mb: 1.5 }}>
                  <Link to="/messages" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                    <Typography variant="body2" sx={{ '&:hover': { color: 'primary.main' } }}>
                      My Messages
                    </Typography>
                  </Link>
                </Box>
                <Box component="li" sx={{ mb: 1.5 }}>
                  <Link to="/prescriptions" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                    <Typography variant="body2" sx={{ '&:hover': { color: 'primary.main' } }}>
                      My Prescriptions
                    </Typography>
                  </Link>
                </Box>
                <Box component="li">
                  <Link to="/medical-records" style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}>
                    <Typography variant="body2" sx={{ '&:hover': { color: 'primary.main' } }}>
                      My Medical Records
                    </Typography>
                  </Link>
                </Box>
              </Box>
            </Paper>
            {/* Add Sponsor */}
            <Paper elevation={1} sx={{ p: 3, borderRadius: 2, width: '100%', minWidth: 0, boxSizing: 'border-box' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Add a Sponsor
              </Typography>
              <AddSponsor />
            </Paper>
            {/* Personal Information Card */}
            <ProfileCard 
              title="Personal Information" 
              icon={<PersonIcon />}
              editable
              onSave={handlePersonalInfoSave}
              sx={{ width: '100%', minWidth: 0, boxSizing: 'border-box', mb: 0 }}
            >
              {(isEditing) => (
                <>
                  <ProfileSection title="Basic Information">
                    <Grid item xs={12} sm={6}>
                      <ProfileField 
                        label="First Name" 
                        value={personalInfo.firstName}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="firstName"
                          value={personalInfo.firstName}
                          onChange={handlePersonalInfoChange}
                        />
                      </ProfileField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ProfileField 
                        label="Last Name" 
                        value={personalInfo.lastName}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="lastName"
                          value={personalInfo.lastName}
                          onChange={handlePersonalInfoChange}
                        />
                      </ProfileField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ProfileField 
                        label="Email" 
                        value={personalInfo.email}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="email"
                          value={personalInfo.email}
                          onChange={handlePersonalInfoChange}
                        />
                      </ProfileField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ProfileField 
                        label="Phone" 
                        value={personalInfo.phone}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="phone"
                          value={personalInfo.phone}
                          onChange={handlePersonalInfoChange}
                        />
                      </ProfileField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ProfileField 
                        label="Date of Birth" 
                        value={personalInfo.dateOfBirth}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="dateOfBirth"
                          type="date"
                          value={personalInfo.dateOfBirth}
                          onChange={handlePersonalInfoChange}
                        />
                      </ProfileField>
                    </Grid>
                  </ProfileSection>
                  
                  <ProfileSection title="Address">
                    <Grid item xs={12}>
                      <ProfileField 
                        label="Street Address" 
                        value={personalInfo.address}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="address"
                          value={personalInfo.address}
                          onChange={handlePersonalInfoChange}
                        />
                      </ProfileField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <ProfileField 
                        label="City" 
                        value={personalInfo.city}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="city"
                          value={personalInfo.city}
                          onChange={handlePersonalInfoChange}
                        />
                      </ProfileField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <ProfileField 
                        label="State" 
                        value={personalInfo.state}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="state"
                          value={personalInfo.state}
                          onChange={handlePersonalInfoChange}
                        />
                      </ProfileField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <ProfileField 
                        label="ZIP Code" 
                        value={personalInfo.zipCode}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="zipCode"
                          value={personalInfo.zipCode}
                          onChange={handlePersonalInfoChange}
                        />
                      </ProfileField>
                    </Grid>
                  </ProfileSection>
                  
                  <ProfileSection title="Emergency Contact" divider={false}>
                    <Grid item xs={12} sm={6}>
                      <ProfileField 
                        label="Name" 
                        value={personalInfo.emergencyContact}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="emergencyContact"
                          value={personalInfo.emergencyContact}
                          onChange={handlePersonalInfoChange}
                        />
                      </ProfileField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ProfileField 
                        label="Phone" 
                        value={personalInfo.emergencyPhone}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="emergencyPhone"
                          value={personalInfo.emergencyPhone}
                          onChange={handlePersonalInfoChange}
                        />
                      </ProfileField>
                    </Grid>
                  </ProfileSection>
                </>
              )}
            </ProfileCard>
            {/* Health Information Card */}
            <ProfileCard 
              title="Health Information" 
              icon={<HealthAndSafetyIcon />}
              editable
              onSave={handleHealthInfoSave}
              color="success"
              sx={{ width: '100%', minWidth: 0, boxSizing: 'border-box', mb: 0 }}
            >
              {(isEditing) => (
                <>
                  <ProfileSection title="Physical Information">
                    <Grid item xs={12} sm={6}>
                      <ProfileField 
                        label="Height" 
                        value={healthInfo.height}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="height"
                          value={healthInfo.height}
                          onChange={handleHealthInfoChange}
                        />
                      </ProfileField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ProfileField 
                        label="Weight" 
                        value={healthInfo.weight}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="weight"
                          value={healthInfo.weight}
                          onChange={handleHealthInfoChange}
                        />
                      </ProfileField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ProfileField 
                        label="Blood Type" 
                        value={healthInfo.bloodType}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="bloodType"
                          value={healthInfo.bloodType}
                          onChange={handleHealthInfoChange}
                        />
                      </ProfileField>
                    </Grid>
                  </ProfileSection>
                  
                  <ProfileSection title="Medical Information">
                    <Grid item xs={12}>
                      <ProfileField 
                        label="Allergies" 
                        value={healthInfo.allergies}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="allergies"
                          value={healthInfo.allergies}
                          onChange={handleHealthInfoChange}
                          multiline
                          rows={2}
                        />
                      </ProfileField>
                    </Grid>
                    <Grid item xs={12}>
                      <ProfileField 
                        label="Current Medications" 
                        value={healthInfo.medications}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="medications"
                          value={healthInfo.medications}
                          onChange={handleHealthInfoChange}
                          multiline
                          rows={2}
                        />
                      </ProfileField>
                    </Grid>
                    <Grid item xs={12}>
                      <ProfileField 
                        label="Medical Conditions" 
                        value={healthInfo.conditions}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="conditions"
                          value={healthInfo.conditions}
                          onChange={handleHealthInfoChange}
                          multiline
                          rows={2}
                        />
                      </ProfileField>
                    </Grid>
                  </ProfileSection>
                  
                  <ProfileSection title="Primary Care" divider={false}>
                    <Grid item xs={12}>
                      <ProfileField 
                        label="Primary Physician" 
                        value={healthInfo.primaryPhysician}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="primaryPhysician"
                          value={healthInfo.primaryPhysician}
                          onChange={handleHealthInfoChange}
                        />
                      </ProfileField>
                    </Grid>
                  </ProfileSection>
                </>
              )}
            </ProfileCard>
            {/* Insurance Information Card */}
            <ProfileCard 
              title="Insurance Information" 
              icon={<SecurityIcon />}
              editable
              onSave={handleInsuranceInfoSave}
              color="info"
              sx={{ width: '100%', minWidth: 0, boxSizing: 'border-box', mb: 0 }}
            >
              {(isEditing) => (
                <>
                  <ProfileSection title="Insurance Details">
                    <Grid item xs={12} sm={6}>
                      <ProfileField 
                        label="Insurance Provider" 
                        value={insuranceInfo.provider}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="provider"
                          value={insuranceInfo.provider}
                          onChange={handleInsuranceInfoChange}
                        />
                      </ProfileField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ProfileField 
                        label="Policy Number" 
                        value={insuranceInfo.policyNumber}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="policyNumber"
                          value={insuranceInfo.policyNumber}
                          onChange={handleInsuranceInfoChange}
                        />
                      </ProfileField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ProfileField 
                        label="Group Number" 
                        value={insuranceInfo.groupNumber}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="groupNumber"
                          value={insuranceInfo.groupNumber}
                          onChange={handleInsuranceInfoChange}
                        />
                      </ProfileField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ProfileField 
                        label="Primary Holder" 
                        value={insuranceInfo.primaryHolder}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="primaryHolder"
                          value={insuranceInfo.primaryHolder}
                          onChange={handleInsuranceInfoChange}
                        />
                      </ProfileField>
                    </Grid>
                  </ProfileSection>
                  
                  <ProfileSection title="Coverage Information" divider={false}>
                    <Grid item xs={12} sm={6}>
                      <ProfileField 
                        label="Coverage Type" 
                        value={insuranceInfo.coverageType}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="coverageType"
                          value={insuranceInfo.coverageType}
                          onChange={handleInsuranceInfoChange}
                        />
                      </ProfileField>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <ProfileField 
                        label="Effective Date" 
                        value={insuranceInfo.effectiveDate}
                        editing={isEditing}
                      >
                        <TextField
                          fullWidth
                          size="small"
                          name="effectiveDate"
                          type="date"
                          value={insuranceInfo.effectiveDate}
                          onChange={handleInsuranceInfoChange}
                        />
                      </ProfileField>
                    </Grid>
                  </ProfileSection>
                </>
              )}
            </ProfileCard>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default Profile;
